import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, CreditCard, LogOut } from "lucide-react";
import { useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router";
import type { MembershipDto, MeResponse } from "../../shared/api-types";
import { api } from "../api/client";
import { useAuth } from "../auth/AuthContext";
import { Alert, ICON, Logo } from "../components/ui";
import { planLine } from "../pages/ClaimPage";
import { type Billing, ProfileForm } from "./settings/BillingPage";

// Activation of a restaurant prepared by Meniova (owner 2026-11-04): until Stripe confirms the card the account shows
// only this page — the preset plan, the company's details and the card, with 0 € to pay today. Back from Stripe it
// waits for the confirmation (the webhook) and then opens the account.

export function ActivationPage({ me, membership }: { me: MeResponse; membership: MembershipDto }) {
  const r = membership.restaurant;
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { refresh, logout } = useAuth();
  const [params] = useSearchParams();
  const back = params.get("checkout");
  const billing = useQuery({
    queryKey: ["billing", r.id],
    queryFn: () => api<Billing>("GET", `/restaurants/${r.id}/billing`),
  });
  const checkout = useMutation({
    mutationFn: () =>
      api<{ url: string }>("POST", `/restaurants/${r.id}/billing/checkout`, { back: "activate" }),
    onSuccess: ({ url }) => window.location.assign(url),
  });
  // Back from Stripe: the confirmation reaches us a few seconds after the redirect; the account opens with it.
  useEffect(() => {
    if (back !== "success") return;
    const t = setInterval(() => void refresh(), 3000);
    return () => clearInterval(t);
  }, [back, refresh]);

  const profile = billing.data?.profile ?? null;
  const days = r.preparedTrialDays ?? 30;
  const plan = r.preparedPlan ?? "standard_year";

  return (
    <div className="act">
      <header className="act-top">
        <Logo className="act-logo" white />
        <button
          type="button"
          className="btn btn-ghost btn-sm act-out"
          onClick={async () => {
            await logout();
            navigate("/login", { replace: true });
          }}
        >
          <LogOut {...ICON} aria-hidden="true" />
          Ieși
        </button>
      </header>
      <main className="act-main" id="main">
        <div className="stack" style={{ gap: 6 }}>
          <span className="greet">Bun venit, {me.user.name.split(" ")[0]}</span>
          <h1 className="h1">Activează {r.name}</h1>
          <p className="muted" style={{ margin: 0 }}>
            Meniul e pregătit de echipa Meniova. Mai sunt doi pași: datele firmei pentru facturi și cardul.
            Azi plătești 0 €; prima plată vine după {days} zile gratuite, iar până atunci anulezi oricând,
            fără costuri.
          </p>
        </div>

        {back === "success" && (
          <Alert tone="success" live>
            Cardul a fost adăugat. Confirmăm abonamentul și deschidem contul în câteva secunde…
          </Alert>
        )}
        {back === "cancel" && (
          <Alert tone="info">Nu s-a adăugat niciun card. Poți încerca din nou oricând.</Alert>
        )}

        <ol className="act-steps">
          <li className="done">
            <span className="act-n">
              <Check size={16} strokeWidth={2.4} aria-hidden="true" />
            </span>
            <div>
              <strong>Planul ales pentru tine</strong>
              <span className="caption muted">{planLine(plan)}</span>
            </div>
          </li>
          <li className={profile ? "done" : "now"}>
            <span className="act-n">
              {profile ? <Check size={16} strokeWidth={2.4} aria-hidden="true" /> : "2"}
            </span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <strong>Datele firmei</strong>
              <span className="caption muted">
                Apar pe facturile lunare sau anuale. Le poți schimba oricând.
              </span>
              {billing.isPending ? (
                <p className="muted" role="status">
                  Se încarcă…
                </p>
              ) : billing.isError ? (
                <Alert tone="error" live>
                  {billing.error.message}
                </Alert>
              ) : (
                <div className="act-form">
                  <ProfileForm
                    rid={r.id}
                    profile={profile}
                    canEdit
                    onSaved={() => void qc.invalidateQueries({ queryKey: ["billing", r.id] })}
                  />
                </div>
              )}
            </div>
          </li>
          <li className={profile ? "now" : ""}>
            <span className="act-n">3</span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <strong>Cardul</strong>
              <span className="caption muted">
                În pagina securizată Stripe. Azi: 0,00 €. Cardul se folosește abia după cele {days} zile
                gratuite.
              </span>
              {checkout.isError && (
                <Alert tone="error" live>
                  {checkout.error.message}
                </Alert>
              )}
              <button
                type="button"
                className="btn btn-primary act-pay"
                disabled={!profile || checkout.isPending || back === "success"}
                onClick={() => checkout.mutate()}
              >
                <CreditCard {...ICON} aria-hidden="true" />
                {checkout.isPending ? "Se deschide Stripe…" : "Adaugă cardul · 0 € azi"}
              </button>
              {!profile && <span className="caption muted">Completează întâi datele firmei.</span>}
            </div>
          </li>
        </ol>
      </main>
    </div>
  );
}
